import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Sparkles, ArrowRight, Check, Star, Flame, Trophy, BookOpen, Heart,
  ShieldCheck, Camera, BarChart3, Bell, Quote, ChevronDown, Users, X,
} from 'lucide-react';
import AmbientLayout from '@/components/AmbientLayout';
import { supabase } from '@/integrations/supabase/client';
import { DOMAIN_META, Domain } from '@/lib/types';
import { track } from '@/lib/analytics';

const DOMAINS: { d: Domain; tag: string }[] = [
  { d: 'language',  tag: 'First words & babble' },
  { d: 'motor',     tag: 'Crawl, climb, leap' },
  { d: 'social',    tag: 'Smiles & connection' },
  { d: 'cognitive', tag: 'Curiosity & problem solving' },
  { d: 'feeding',   tag: 'Tastes & textures' },
  { d: 'selfcare',  tag: 'Little independence' },
];

const FEATURES = [
  { icon: Sparkles,  title: 'Daily play prompts',     body: 'A bite sized idea each morning, tuned to your child’s age and the things you care about most.' },
  { icon: BookOpen,  title: 'Evidence-backed milestones', body: 'Every milestone is sourced from CDC and AAP guidance, no internet folklore and no anxiety bait.' },
  { icon: Camera,    title: 'Memory keepsake',         body: 'Add a note or photo to any milestone. Years from now, it becomes a love letter to their childhood.' },
  { icon: BarChart3, title: 'Gentle progress, never comparison', body: 'See where your child sits within the typical CDC range ever ranked against other kids.' },
  { icon: Bell,      title: 'Whisper-quiet reminders', body: 'A nudge when something new is likely emerging. Off by default you’re in charge.' },
  { icon: ShieldCheck, title: 'Private by design',     body: 'Your data is yours. End-to-end encrypted in transit, never sold, never trained on.' },
];

const TESTIMONIALS = [
  { quote: 'Pebble made me notice things I would’ve missed. The first time Iris pointed at the moon I almost cried logging it.', name: 'Maya', sub: 'mom of Iris, 14 months' },
  { quote: 'Finally a tracker that doesn’t make me feel behind. It feels like a friend, not a checklist.', name: 'Daniel', sub: 'dad of twins, 9 months' },
  { quote: 'The science citations sold me. I trust this more than three pediatrician forums combined.', name: 'Priya', sub: 'mom of Arjun, 2.5 years' },
];

const FAQ = [
  { q: 'Is Pebble free?', a: 'Yes — the full milestone roadmap, daily prompts, and your private memory keepsake all yours, no card needed.' },
  { q: 'Will it make me anxious about my child’s progress?', a: 'The opposite. We never compare your child to others. We show the typical CDC age range and celebrate every step — early, on-time, or later.' },
  { q: 'Where do the milestones come from?', a: 'CDC Learn the Signs (2022 revision) and AAP Bright Futures. Every milestone links to its primary source.' },
  { q: 'Who can see my data?', a: 'Only you. Your notes and photos are private to your account. We never sell data or use it to train AI.' },
];

export default function Landing() {
  const nav = useNavigate();
  const reduce = useReducedMotion();
  const [signedIn, setSignedIn] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [showBeta, setShowBeta] = useState(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem('pebble.betaDisclaimerDismissed') !== '1';
  });

  useEffect(() => {
    // Defer auth check until after first paint so it doesn't compete with LCP.
    let sub: { subscription: { unsubscribe: () => void } } | null = null;
    const run = () => {
      supabase.auth.getSession().then(({ data }) => {
        if (data.session) {
          nav('/home', { replace: true });
        } else {
          setSignedIn(false);
        }
      });
      const res = supabase.auth.onAuthStateChange((_e, s) => {
        if (s) nav('/home', { replace: true });
        else setSignedIn(false);
      });
      sub = res.data;
    };
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    const id = w.requestIdleCallback ? w.requestIdleCallback(run) : window.setTimeout(run, 0);
    return () => {
      if (w.requestIdleCallback) cancelIdleCallback(id);
      else clearTimeout(id);
      sub?.subscription.unsubscribe();
    };
  }, [nav]);

  const dismissBeta = () => {
    setShowBeta(false);
    try { localStorage.setItem('pebble.betaDisclaimerDismissed', '1'); } catch { /* ignore */ }
    track('beta_disclaimer_dismissed');
  };

  const ctaTo = signedIn ? '/home' : '/auth';
  const ctaLabel = signedIn ? 'Open your dashboard' : 'Start free';

  return (
    <AmbientLayout>
      {/* Top nav */}
      <nav className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-base shadow-soft"
            style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--domain-cognitive)))' }}
          >
            ✨
          </span>
          <span className="font-display text-lg">Pebble</span>
        </div>
        <button
          onClick={() => nav(ctaTo)}
          className="rounded-full bg-foreground/5 hover:bg-foreground/10 px-3.5 py-1.5 text-xs font-medium transition"
        >
          {signedIn ? 'Dashboard' : 'Sign in'}
        </button>
      </nav>

      {/* Friends-only beta disclaimer */}
      {showBeta && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="glass mb-6 flex items-start gap-3 p-3.5"
          style={{
            background: 'linear-gradient(135deg, hsl(var(--primary) / 0.12), hsl(var(--domain-cognitive) / 0.10))',
            borderColor: 'hsl(var(--primary) / 0.30)',
          }}
          role="status"
        >
          <span
            className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
            style={{ background: 'hsl(var(--primary) / 0.18)', color: 'hsl(var(--primary))' }}
          >
            <Users className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0 flex-1 text-xs leading-relaxed text-foreground/80">
            <span className="font-semibold text-foreground">Friends-only beta.</span>{' '}
            Pebble is in early testing with a small circle of families. Some community-powered
            features (like Parent Pulse) show curated preview snippets while our data scale grows.
            Your feedback shapes what we ship next.
          </div>
          <button
            onClick={dismissBeta}
            aria-label="Dismiss beta notice"
            className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-foreground/10 hover:text-foreground transition"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </motion.div>
      )}

      {/* HERO */}
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="text-center mb-10"
      >
        <span className="inline-flex items-center gap-1.5 rounded-full bg-foreground/5 px-3 py-1 text-[11px] font-medium text-foreground/70 mb-5">
          <Sparkles className="h-3 w-3 text-primary" /> Science backed · CDC sourced
        </span>
        <h1 className="font-display text-[2.6rem] leading-[1.05] tracking-tight">
          Every little step,
          <br />
          <span
            className="bg-clip-text text-transparent"
            style={{ backgroundImage: 'linear-gradient(120deg, hsl(var(--primary)), hsl(var(--domain-cognitive)) 60%, hsl(var(--domain-language)))' }}
          >
            beautifully remembered.
          </span>
        </h1>
        <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-sm mx-auto">
          Pebble helps you notice, celebrate, and remember the milestones that make your child uniquely theirs no comparison, no anxiety, ever.
        </p>

        <div className="mt-7 flex flex-col gap-2.5">
          <Link
            to={ctaTo}
            className="group relative overflow-hidden inline-flex items-center justify-center gap-2 rounded-2xl px-7 py-4 font-medium text-primary-foreground shadow-lift transition hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(120deg, hsl(var(--primary)), hsl(var(--domain-cognitive)))' }}
          >
            <span className="relative z-10">{ctaLabel}</span>
            <ArrowRight className="h-4 w-4 relative z-10 transition-transform group-hover:translate-x-0.5" />
            <span
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ background: 'linear-gradient(120deg, hsl(var(--domain-cognitive)), hsl(var(--primary)))' }}
            />
          </Link>
          <p className="text-[11px] text-muted-foreground">Free forever · No credit card · 30 second setup</p>
        </div>
      </motion.section>

      {/* HERO PREVIEW CARD — fake dashboard */}
      <motion.section
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.15, ease: 'easeOut' }}
        className="relative mb-12"
      >
        {/* Floating stat chips */}
        {!reduce && (
          <>
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -left-2 -top-3 z-20 glass px-3 py-2 flex items-center gap-1.5 text-xs font-medium shadow-soft"
            >
              <Flame className="h-3.5 w-3.5 text-primary" /> 12-day streak
            </motion.div>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
              className="absolute -right-2 top-8 z-20 glass px-3 py-2 flex items-center gap-1.5 text-xs font-medium shadow-soft"
              style={{ color: 'hsl(var(--primary))' }}
            >
              <Sparkles className="h-3.5 w-3.5" /> +75 XP
            </motion.div>
          </>
        )}

        <div className="glass-lift p-5">
          {/* Mini child header */}
          <div className="flex items-start gap-3">
            <div className="text-4xl animate-wiggle">🧒</div>
            <div className="min-w-0 flex-1">
              <h3 className="font-display text-xl leading-tight">Olive</h3>
              <p className="text-xs text-muted-foreground">14 months old · Level 3</p>
              <span
                className="mt-1 inline-block rounded-full px-2.5 py-0.5 text-[10px] font-medium"
                style={{ background: 'hsl(var(--primary) / 0.18)', color: 'hsl(var(--primary))' }}
              >
                Bold Explorer
              </span>
            </div>
            <div className="text-right">
              <div className="font-display text-lg leading-none">68%</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">journey</div>
            </div>
          </div>

          {/* Mini XP bar */}
          <div className="mt-4">
            <div className="xp-bar h-2.5 rounded-full">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: '68%' }}
                transition={{ duration: 1.2, delay: 0.4, ease: 'easeOut' }}
                className="xp-bar-fill h-full rounded-full"
              />
            </div>
          </div>

          {/* Mini milestone rows */}
          <div className="mt-4 space-y-2">
            {[
              { d: 'language' as Domain,  t: 'Says “mama” with meaning', done: true  },
              { d: 'motor'    as Domain,  t: 'Takes first independent steps', done: true  },
              { d: 'cognitive' as Domain, t: 'Stacks two blocks',         done: false },
            ].map((m, i) => {
              const meta = DOMAIN_META[m.d];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                  className="flex items-center gap-3 rounded-2xl bg-foreground/5 p-2.5"
                >
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-base"
                    style={{ background: `hsl(${meta.hsl} / 0.25)`, border: `1px solid hsl(${meta.hsl} / 0.4)` }}
                  >
                    {meta.emoji}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div
                      className="text-[10px] uppercase tracking-wider font-semibold"
                      style={{ color: `hsl(${meta.hsl})`, filter: 'brightness(0.7)' }}
                    >
                      {meta.label}
                    </div>
                    <div className="text-sm leading-snug truncate">{m.t}</div>
                  </div>
                  <div
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                      m.done ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {m.done ? <Check className="h-3.5 w-3.5" /> : <Star className="h-3 w-3" />}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* SOCIAL PROOF STATS */}
      <section className="mb-12 grid grid-cols-3 gap-2">
        {[
          { v: '6',    l: 'Domains' },
          { v: '100%', l: 'CDC SOURCED' },
          { v: '25+',  l: 'Milestones' },
        ].map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.07 }}
            className="glass p-4 text-center"
          >
            <div className="font-display text-2xl">{s.v}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{s.l}</div>
          </motion.div>
        ))}
      </section>

      {/* DOMAINS */}
      <section className="mb-12">
        <SectionLabel icon={<Heart className="h-3.5 w-3.5" />} label="Six domains, one story" />
        <h2 className="font-display text-2xl leading-tight mb-4">A whole-child view of growth.</h2>
        <div className="grid grid-cols-2 gap-2.5">
          {DOMAINS.map((x, i) => {
            const meta = DOMAIN_META[x.d];
            return (
              <motion.div
                key={x.d}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ delay: i * 0.05 }}
                className="glass p-3.5 hover:-translate-y-0.5 transition-transform"
                style={{
                  background: `linear-gradient(135deg, hsl(${meta.hsl} / 0.22), hsl(var(--glass-bg) / 0.6))`,
                  borderColor: `hsl(${meta.hsl} / 0.35)`,
                }}
              >
                <div className="text-2xl mb-1">{meta.emoji}</div>
                <div className="font-display text-sm">{meta.label}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{x.tag}</div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* FEATURES */}
      <section className="mb-12">
        <SectionLabel icon={<Sparkles className="h-3.5 w-3.5" />} label="What you get" />
        <h2 className="font-display text-2xl leading-tight mb-4">Built for the moments you don’t want to forget.</h2>
        <div className="space-y-2.5">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ delay: i * 0.04 }}
                className="glass p-4 flex gap-3"
              >
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                  style={{ background: 'hsl(var(--primary) / 0.15)', color: 'hsl(var(--primary))' }}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-display text-base leading-snug">{f.title}</div>
                  <div className="text-sm text-muted-foreground mt-0.5 leading-relaxed">{f.body}</div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mb-12">
        <SectionLabel icon={<Trophy className="h-3.5 w-3.5" />} label="How it works" />
        <h2 className="font-display text-2xl leading-tight mb-4">Three small steps. A keepsake for life.</h2>
        <div className="space-y-3">
          {[
            { n: '01', t: 'Tell us about your little one', b: 'Name, birthday, and a few areas you care about. Takes 30 seconds.' },
            { n: '02', t: 'Get a personalized roadmap',     b: '25+ age-appropriate milestones, organized into gentle bands you can explore.' },
            { n: '03', t: 'Celebrate as it happens',         b: 'Tap to log, add a photo or note. Watch the XP bar fill and the memories pile up.' },
          ].map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ delay: i * 0.08 }}
              className="glass p-4 flex gap-3"
            >
              <div
                className="font-display text-2xl leading-none"
                style={{ color: 'hsl(var(--primary))' }}
              >
                {s.n}
              </div>
              <div className="min-w-0">
                <div className="font-display text-base">{s.t}</div>
                <div className="text-sm text-muted-foreground mt-0.5 leading-relaxed">{s.b}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mb-12">
        <SectionLabel icon={<Quote className="h-3.5 w-3.5" />} label="Loved by parents" />
        <h2 className="font-display text-2xl leading-tight mb-4">A quiet companion. Never a coach.</h2>
        <div className="space-y-3">
          {TESTIMONIALS.map((t, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ delay: i * 0.08 }}
              className="glass p-5"
            >
              <div className="flex gap-0.5 mb-2" style={{ color: 'hsl(var(--primary))' }}>
                {Array.from({ length: 5 }).map((_, j) => (
                  <Star key={j} className="h-3.5 w-3.5 fill-current" />
                ))}
              </div>
              <blockquote className="font-display text-base leading-snug">"{t.quote}"</blockquote>
              <figcaption className="mt-3 text-xs text-muted-foreground">
                <span className="font-medium text-foreground/80">{t.name}</span> · {t.sub}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="mb-12">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-lift p-6 text-center"
          style={{ background: 'linear-gradient(135deg, hsl(var(--domain-cognitive) / 0.25), hsl(var(--primary) / 0.15))' }}
        >
          <span className="inline-flex items-center gap-1 rounded-full bg-foreground/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider">
            Free forever
          </span>
          <h3 className="font-display text-2xl mt-3 leading-tight">Start with everything that matters.</h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
            The full milestone roadmap, daily prompts, and your private memory keepsake — all yours, no card needed.
          </p>
          <Link
            to={ctaTo}
            className="mt-5 inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-3.5 font-medium text-primary-foreground shadow-soft hover:shadow-lift transition"
          >
            {ctaLabel} <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="mb-12">
        <SectionLabel icon={<BookOpen className="h-3.5 w-3.5" />} label="Questions" />
        <h2 className="font-display text-2xl leading-tight mb-4">The small print, plainly said.</h2>
        <div className="space-y-2">
          {FAQ.map((f, i) => {
            const open = openFaq === i;
            return (
              <div key={i} className="glass overflow-hidden">
                <button
                  onClick={() => setOpenFaq(open ? null : i)}
                  className="flex w-full items-center justify-between gap-3 p-4 text-left"
                >
                  <span className="font-display text-base">{f.q}</span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
                  />
                </button>
                {open && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <p className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mb-8 text-center">
        <h2 className="font-display text-2xl leading-tight">Begin your journey today.</h2>
        <p className="text-sm text-muted-foreground mt-2 mb-5">It’s free, it’s private, and it takes 30 seconds.</p>
        <Link
          to={ctaTo}
          className="inline-flex items-center justify-center gap-2 rounded-2xl px-7 py-4 font-medium text-primary-foreground shadow-lift transition hover:-translate-y-0.5"
          style={{ background: 'linear-gradient(120deg, hsl(var(--primary)), hsl(var(--domain-cognitive)))' }}
        >
          {ctaLabel} <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      <footer className="mt-10 text-center text-[11px] text-muted-foreground space-y-2">
        <div className="space-x-1.5">
          <Link to="/privacy" className="hover:text-foreground transition underline-offset-2 hover:underline">
            Privacy Policy
          </Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-foreground transition underline-offset-2 hover:underline">
            Terms of Service
          </Link>
          <span>·</span>
          <a
            href="mailto:feedback@pebblemilestones.com"
            className="hover:text-foreground transition underline-offset-2 hover:underline"
          >
            Contact
          </a>
        </div>
        <div>© {new Date().getFullYear()} Pebble · Made with care for little humans.</div>
      </footer>
    </AmbientLayout>
  );
}

function SectionLabel({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full bg-foreground/5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-foreground/60 mb-3">
      {icon} {label}
    </div>
  );
}
